/**
 * bimIfcAsset.ts — ONE parser for an imported IFC object, shared by every layer
 * that puts a file the operator uploaded into the scene (card `t_773c82ca`).
 *
 * Before this module the app had two copies of the same forty lines — the
 * device-model layer's `source: 'ifc'` path (`useBimDeviceModels`) and the
 * static-object layer (`useBimPlacedObjects`) — each opening the bytes with
 * web-ifc, merging each element's placed geometries per material colour into one
 * mesh, and measuring the file's own bounding box. "One asset pool for devices
 * and static objects" is only true if the two layers read a file the SAME way,
 * so both now call this function; a sofa uploaded once is parsed once, whichever
 * layer asks for it.
 *
 * The parse is deliberately transport-free: the caller fetches the bytes
 * (`routedFetch` in the app, a hub GET in the rigs) and hands them over. What
 * comes back is in the FILE's own frame — the caller decides where it stands
 * (`useBimPlacedObjects` lands the box centre on the entry's translation). The
 * served model file is never written here or anywhere: this module only reads.
 *
 * Node-safe on purpose (no `window`, no Vue): the unit test parses a real IFC
 * fixture with the same code the browser runs, and web-ifc's own package
 * resolution hands Node the `web-ifc-api-node` build and the browser the wasm
 * build. A caller that has a base path for the wasm (the app, on a hub-served
 * page or in Electron) passes it in; Node resolves its own.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** A geometry as web-ifc's `StreamAllMeshes` hands it over. */
interface IfcPlacedGeometryLike {
  color: { x: number; y: number; z: number; w: number };
  geometryExpressID: number;
  flatTransformation: number[];
}

interface IfcStreamedMeshLike {
  expressID: number;
  geometries: {
    size: () => number;
    get: (index: number) => IfcPlacedGeometryLike;
  };
}

/** What one imported IFC object is, once parsed. */
export interface BimIfcAssetGeometry {
  /** One mesh per (element, material) of the file, in the FILE's own frame. */
  meshes: THREE.Mesh[];
  /** The box centre of everything the file draws, or null when it draws nothing. */
  center: [number, number, number] | null;
  /** Its extent (x, y, z), or null when the file draws nothing. */
  size: [number, number, number] | null;
  /** How many (element, material) buckets the meshes were merged into. */
  materialCount: number;
  /** How many placed geometries the file carried, before merging. */
  placedGeometries: number;
}

export interface ParseBimIfcAssetOptions {
  /**
   * Marks every mesh with `userData[tag] = true`, which is how the rigs find a
   * layer's own objects (`__bimPlacedObject`, `__bimDeviceModel`).
   */
  tag?: string;
  /**
   * The directory web-ifc's wasm is fetched from. Omitted, the build resolves
   * its own — which is what Node (the tests) wants; a page passes its own base.
   */
  wasmPath?: string;
  /**
   * `'auto'` (default) lets an opaque part cast a shadow; `'none'` keeps the
   * device-model layer's own look, where an imported model never shadows.
   */
  castShadow?: 'auto' | 'none';
}

/**
 * The base path web-ifc's wasm is served from, `file://`-safe: the page's own
 * origin, or the directory of the document when the app runs from a file
 * (`Electron`'s packaged `file://` load).
 */
export function wasmBasePath(): string {
  const origin = window.location.origin;
  const isFileScheme =
    origin === 'null' || origin === '' || origin === 'file://' || window.location.protocol === 'file:';
  if (!isFileScheme) return `${origin}/`;
  const href = window.location.href.split('#')[0];
  return href.substring(0, href.lastIndexOf('/') + 1);
}

function materialOf(color: { x: number; y: number; z: number; w: number }): THREE.MeshStandardMaterial {
  const isTransparent = color.w < 0.999;
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(color.x, color.y, color.z),
    roughness: 0.85,
    metalness: 0,
    side: THREE.DoubleSide,
    transparent: isTransparent,
    // A barely-there part stays visible enough to read as a part of the object,
    // and an opaque one writes depth exactly as an opaque material should.
    opacity: Math.min(Math.max(color.w, 0.05), 1),
    depthWrite: !isTransparent,
  });
}

/**
 * Open one imported IFC's bytes and hand back its meshes, merged per
 * (element, material) — the house loader's own recipe (`useBimScene`), which is
 * what keeps an uploaded object's look identical to the served model's.
 *
 * A file that carries no drawable geometry is not a throw: the caller gets
 * `meshes: []` (and `center`/`size` null), so a layer can say "this file has
 * nothing to draw" instead of failing to load at all.
 */
export async function parseBimIfcAsset(
  bytes: Uint8Array,
  options: ParseBimIfcAssetOptions = {},
): Promise<BimIfcAssetGeometry> {
  const { IfcAPI } = await import('web-ifc');
  const api = new IfcAPI();
  if (options.wasmPath !== undefined) api.SetWasmPath(options.wasmPath, true);
  await api.Init();

  const modelID = api.OpenModel(bytes, { COORDINATE_TO_ORIGIN: false });

  interface Bucket {
    geoms: THREE.BufferGeometry[];
    color: { x: number; y: number; z: number; w: number };
  }
  const buckets = new Map<string, Bucket>();
  let placedGeometries = 0;
  api.StreamAllMeshes(modelID, (ifcMesh: IfcStreamedMeshLike) => {
    const geometries = ifcMesh.geometries;
    for (let i = 0; i < geometries.size(); i += 1) {
      const placed = geometries.get(i);
      const c = placed.color;
      if (c.w <= 0.01) continue; // fully transparent air-space volumes
      const geomData = api.GetGeometry(modelID, placed.geometryExpressID);
      const rawVerts = api.GetVertexArray(geomData.GetVertexData(), geomData.GetVertexDataSize());
      const rawIdx = api.GetIndexArray(geomData.GetIndexData(), geomData.GetIndexDataSize());
      const posArr = new Float32Array(rawVerts.length / 2);
      const normArr = new Float32Array(rawVerts.length / 2);
      for (let j = 0; j < rawVerts.length; j += 6) {
        const p = (j / 6) * 3;
        posArr[p] = rawVerts[j];
        posArr[p + 1] = rawVerts[j + 1];
        posArr[p + 2] = rawVerts[j + 2];
        normArr[p] = rawVerts[j + 3];
        normArr[p + 1] = rawVerts[j + 4];
        normArr[p + 2] = rawVerts[j + 5];
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
      geometry.setAttribute('normal', new THREE.BufferAttribute(normArr, 3));
      geometry.setIndex(new THREE.BufferAttribute(rawIdx, 1));
      geometry.applyMatrix4(new THREE.Matrix4().fromArray(Array.from(placed.flatTransformation)));
      const colourKey = `${c.x.toFixed(4)},${c.y.toFixed(4)},${c.z.toFixed(4)},${c.w.toFixed(4)}`;
      const key = `${ifcMesh.expressID}::${colourKey}`;
      const bucket = buckets.get(key);
      if (bucket) bucket.geoms.push(geometry);
      else buckets.set(key, { geoms: [geometry], color: { x: c.x, y: c.y, z: c.z, w: c.w } });
      placedGeometries += 1;
      geomData.delete();
    }
  });

  const meshes: THREE.Mesh[] = [];
  const union = new THREE.Box3();
  for (const bucket of buckets.values()) {
    // One material per bucket by construction (the colour is part of the key),
    // so the merge never has to carry groups.
    const geometry =
      bucket.geoms.length === 1 ? bucket.geoms[0] : (mergeGeometries(bucket.geoms, false) ?? bucket.geoms[0]);
    const mesh = new THREE.Mesh(geometry, materialOf(bucket.color));
    mesh.castShadow = options.castShadow === 'none' ? false : bucket.color.w >= 0.999;
    mesh.receiveShadow = true;
    if (options.tag) mesh.userData[options.tag] = true;
    union.expandByObject(mesh);
    meshes.push(mesh);
  }
  try {
    api.CloseModel(modelID);
  } catch {
    /* a closed model is not an error worth failing the load for */
  }

  const box = union.isEmpty() ? null : union;
  return {
    meshes,
    center: box ? [(box.min.x + box.max.x) / 2, (box.min.y + box.max.y) / 2, (box.min.z + box.max.z) / 2] : null,
    size: box ? [box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z] : null,
    materialCount: buckets.size,
    placedGeometries,
  };
}

/** Drop a parsed object's meshes (geometry + material), e.g. on unmount. */
export function disposeBimIfcMeshes(meshes: readonly THREE.Mesh[]): void {
  for (const mesh of meshes) {
    mesh.removeFromParent();
    mesh.geometry.dispose();
    const material = mesh.material;
    if (Array.isArray(material)) material.forEach((entry) => entry.dispose());
    else material.dispose();
  }
}
